import { BACKEND_API_BASE_URL } from "utils/constants";

const ACCESS_KEY = "access_token";
const REFRESH_KEY = "refresh_token";
export const AUTH_EXPIRED_EVENT = "padihat:auth-expired";
export const AUTH_TOKEN_EVENT = "padihat:auth-token";

let refreshPromise = null;

export const resolveApiUrl = (path) =>
  /^https?:\/\//i.test(path) ? path : `${BACKEND_API_BASE_URL}${path}`;

const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

const sessionExpired = () => {
  clearTokens();
  const error = new Error("Your session has expired. Please sign in again.");
  error.status = 401;
  return error;
};

// Only the server refusing the refresh token (400/401) ends the session. A network error or a 5xx - the backend
// restarting for a deploy - leaves the tokens alone, so the next request refreshes again instead of signing out.
const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) throw sessionExpired();

  refreshPromise = fetch(resolveApiUrl("/api/auth/refresh/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  })
    .then(async (response) => {
      if (response.status === 400 || response.status === 401) throw sessionExpired();
      if (!response.ok) throw new Error(`Session refresh failed: ${response.status} ${response.statusText}`.trim());
      const data = await response.json();
      if (!data.access) throw new Error("Session refresh returned no access token");
      localStorage.setItem(ACCESS_KEY, data.access);
      if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
      window.dispatchEvent(new CustomEvent(AUTH_TOKEN_EVENT, { detail: data.access }));
      return data.access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const responseError = async (response) => {
  let message = `Request failed: ${response.status} ${response.statusText}`.trim();
  let body;
  try {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await response.clone().json();
      message = data.message || data.detail || data.error || message;
      body = data;
    }
  } catch {
    // Retain the transport-level fallback.
  }
  const error = new Error(message);
  error.status = response.status;
  // The whole JSON answer, for callers that show more than its message (e.g.
  // the list of problems a refused lab run start carries).
  if (body !== undefined) error.data = body;
  return error;
};

export async function authenticatedFetch(path, options = {}, retry = true) {
  const headers = new Headers(options.headers || {});
  const token = localStorage.getItem(ACCESS_KEY);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(resolveApiUrl(path), { ...options, headers });
  if (response.status === 401 && retry) {
    // With no refresh token (signed out in another tab) this ends the session too, so the page goes to the sign-in
    // screen instead of staying up with every poll failing.
    await refreshAccessToken();
    return authenticatedFetch(path, options, false);
  }
  if (!response.ok) throw await responseError(response);
  return response;
}

export async function requestJson(path, options = {}) {
  const response = await authenticatedFetch(path, options);
  if (response.status === 204) return null;
  return response.json();
}

export async function requestBlob(path, options = {}) {
  const response = await authenticatedFetch(path, options);
  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get("content-disposition") || "",
  };
}
